import { effect, inject, Provider, signal } from '@angular/core';
import { GeneralInfo, Parcel, ParcelProperty } from '@core/api/angular';
import {
  ADVANCED_SEARCH_OPTIONS,
  FeatureService,
  GEOSERVER_CRS,
  MapService,
} from '@lassi/webapp-shared';
import {
  patchState,
  signalStore,
  type,
  withHooks,
  withMethods,
  withProps,
} from '@ngrx/signals';
import {
  addEntity,
  entityConfig,
  removeAllEntities,
  removeEntity,
  withEntities,
} from '@ngrx/signals/entities';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { IParcelsWithPropertiesAndGeometry } from '@lassi/components';
import { EmbeddedMapOrigins } from '@lassi/config';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import L, { type GeoJSON, Point } from 'leaflet';
import 'proj4leaflet';
import { map, pipe, tap, withLatestFrom } from 'rxjs';

export interface IdentifiedParcelData {
  general: GeneralInfo;
  parcels: IParcelsWithPropertiesAndGeometry[];
  geometry: GeoJSON.MultiPolygon;
  coords: Point;
}

export enum SpearMapType {
  VIEW = 'VIEW',
  SELECT = 'SEARCH',
}

export const SPEAR_SELECT_ADVANCED_SEARCH_OPTIONS_PROVIDER: Provider = [
  {
    provide: ADVANCED_SEARCH_OPTIONS,
    useValue: [
      {
        label: 'Address',
        href: 'address',
      },
      {
        label: 'Lot on Plan',
        href: 'lot-plan',
      },
      {
        label: 'Crown Description',
        href: 'crown-description',
      },
    ],
  },
];

export enum SpearIncomingMessageType {
  READY = 'READY',
  INVALID_REQUEST = 'INVALID_REQUEST',
  PFI = 'PFI',
}

export enum SpearOutgoingMessageType {
  NOT_FOUND = 'NOT_FOUND',
  READY = 'READY',
  INVALID_REQUEST = 'INVALID_REQUEST',
  COMPLETE = 'COMPLETE',
}

export interface SpearPfiMessage {
  type: SpearIncomingMessageType.PFI;
  body: Partial<{
    parcelPFIs: string; // comma delimited string
    propertyPFIs: string; // comma delimited string
  }>;
}

export interface SpearMessageWithoutBody {
  type:
    | SpearOutgoingMessageType.READY
    | SpearOutgoingMessageType.INVALID_REQUEST;
}

export interface SpearCompleteMessage {
  type: SpearOutgoingMessageType.COMPLETE;
  spis: string[]; // spis
}

export interface SpearNotFoundMessage {
  type: SpearOutgoingMessageType.NOT_FOUND;
  notFound: Partial<{ parcelPFIs: string[]; propertyPFIs: string[] }>;
}

export interface ISelectedParcel extends Parcel {
  geometry: GeoJSON.MultiPolygon;
  properties?: ParcelProperty[];
  general?: GeneralInfo;
  coords?: Point;
}

const parcelSelectConfig = entityConfig({
  entity: type<ISelectedParcel>(),
  selectId: (parcel) => parcel.parcel_pfi,
});

const SELECTED_GEOJSON_OPTIONS = {
  style: {
    className: 'pointer-events-none',
    color: 'purple',
    fillColor: 'purple',
    fillRule: 'nonzero' as const,
  },
};

export const ParcelSelectStore = signalStore(
  withEntities(parcelSelectConfig),
  withProps(() => ({
    SPEAR_ORIGIN: inject(EmbeddedMapOrigins).pipe(
      map((origins) => origins.spear_origin),
    ),
    feature: inject(FeatureService),
    spearParcelSelectedLayer: inject(MapService).spearParcelSelectedLayer,
    identifiedParcelData: signal<IdentifiedParcelData | null>(null),
  })),
  withMethods(({ SPEAR_ORIGIN }) => ({
    sendMessage: rxMethod<
      SpearMessageWithoutBody | SpearCompleteMessage | SpearNotFoundMessage
    >(
      pipe(
        withLatestFrom(SPEAR_ORIGIN),
        tap(([message, origin]) => {
          if (!origin) {
            console.warn('Unknown SPEAR origin, cannot send message');
            return;
          }

          let body = '';
          switch (message.type) {
            case SpearOutgoingMessageType.COMPLETE:
              body = message.spis.join(',');
              break;

            case SpearOutgoingMessageType.NOT_FOUND:
              body = JSON.stringify(message.notFound);
              break;
          }

          window.parent.postMessage(
            JSON.stringify({ type: message.type, body }),
            origin,
          );
          console.debug(
            'Sending message to SPEAR host',
            JSON.stringify({ type: message.type, body }),
            origin,
          );
        }),
      ),
    ),
  })),
  withMethods((store) => ({
    addParcel: (parcel: ISelectedParcel) => {
      patchState(store, addEntity(parcel, parcelSelectConfig));
    },
    removeParcel: (parcel: Parcel) => {
      patchState(store, removeEntity(parcel.parcel_pfi));
    },
    clear: () => {
      patchState(store, removeAllEntities());
    },
    finishSelecting: () => {
      store.sendMessage({
        type: SpearOutgoingMessageType.COMPLETE,
        spis: store.entities().map((p) => p.spi!),
      });
    },
    setIdentifiedParcelData: (data: IdentifiedParcelData) => {
      store.identifiedParcelData.set(data);
    },
    clearIdentifiedParcelData: () => {
      store.identifiedParcelData.set(null);
    },
  })),
  withHooks(({ entities, spearParcelSelectedLayer }) => ({
    onInit: () => {
      effect(() => {
        spearParcelSelectedLayer.clearLayers();

        const vPfis = new Set<string>(); // to deduplicate parcel_view_pfi
        for (const parcel of entities()) {
          if (vPfis.has(parcel.parcel_view_pfi!)) {
            continue;
          }

          L.Proj.geoJson(
            {
              ...parcel.geometry,
              crs: GEOSERVER_CRS,
            } as never,
            SELECTED_GEOJSON_OPTIONS,
          ).addTo(spearParcelSelectedLayer);

          vPfis.add(parcel.parcel_view_pfi!);
        }
      });
    },
  })),
);
